import React from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import EmpLayout from '@/components/empreendimentos/layout/EmpLayout';
import EmpHome from '@/components/empreendimentos/layout/EmpHome';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantTier } from '@/hooks/useTenantTier';
import { ShieldAlert, Loader2 } from 'lucide-react';

const sectionLabels: Record<string, string> = {
  contratos: 'Contratos',
  execucao: 'Portal da Execução',
  medicoes: 'Portal de Medições',
  indicadores: 'Indicadores',
  alertas: 'Alertas',
  relatorios: 'Relatórios',
  auditoria: 'Auditoria',
  usuarios: 'Usuários',
  configuracoes: 'Configurações',
  manual: 'Manual do Usuário',
  mapa: 'Inteligência Geoespacial',
  monitoramento: 'Monitoramento Operacional',
  clusters: 'Clusters',
  arquivo: 'Arquivo & Data Books',
  'quality-gate': 'Portal da Qualidade',
  'planning-gate': 'Portal da Planejamento',
  portfolio: 'Painel Executivo',
  sandbox: 'Sandbox de Testes',
  'qa-dashboard': 'QA Dashboard',
  'sla-uptime': 'SLA & Uptime',
  integracao: 'Integração ERP',
  bim: 'Visualização BIM',
  'risk-analytics': 'Analytics Operacional',
  'timeline-decisoes': 'Timeline de Decisões',
  'rkg-governance': 'Governança RKG',
  'siafo-import': 'Import SIAFO',
  inteligencia: 'Inteligência de Risco',
  'convites-if': 'Convites para IFs',
  observabilidade: 'Observabilidade',
  'ferramentas-dev': 'Ferramentas Dev',
};

const sectionSubtitles: Record<string, string> = {
  '': 'Previsibilidade com lastro — ponto de entrada da plataforma',
  monitoramento: 'Visão consolidada da operação diária',
  contratos: 'Cadastro, aditivos e gestão contratual',
  execucao: 'Registro e validação de serviços executados',
  medicoes: 'Boletins, glosas e acompanhamento financeiro',
  indicadores: 'SPI, CPI, desvios e alertas de desempenho',
  alertas: 'Monitoramento de riscos e notificações',
  relatorios: 'Geração e exportação de relatórios',
  auditoria: 'Trilha de auditoria e rastreabilidade',
  usuarios: 'Gestão de acessos e permissões',
  configuracoes: 'Parâmetros e preferências do sistema',
  manual: 'Documentação técnica e guias operacionais',
  mapa: 'Análise territorial de ativos, contratos e clusters operacionais',
  clusters: 'Agrupamento regional de contratos',
  arquivo: 'Documentos, anexos e data books',
  'quality-gate': 'Checklists e verificações de qualidade',
  'planning-gate': 'Planejamento e cronograma de atividades',
  portfolio: 'Visão consolidada de desempenho, indicadores e riscos da carteira',
  sandbox: 'Ambiente de testes e validação',
  'qa-dashboard': 'Indicadores de qualidade e cobertura',
  'sla-uptime': 'Disponibilidade e níveis de serviço',
  integracao: 'Conexão com sistemas ERP externos',
  bim: 'Visualização de modelos BIM',
  'risk-analytics': 'Análise operacional de KPIs, tendências e simulações Monte Carlo',
  'timeline-decisoes': 'Rastreabilidade completa de ações, aprovações e decisões contratuais',
  'rkg-governance': 'Observabilidade, auditoria e validação do motor preditivo',
  'siafo-import': 'Migração histórica — importação única do pacote de dados gerado pelo SIAFO',
  inteligencia: 'Visão consolidada de risco e criticidade da carteira de contratos',
  'convites-if': 'Geração e gestão de tokens de acesso para Instituições Financeiras',
  observabilidade: 'SLA, pipeline e governança do motor preditivo em tempo real',
  'ferramentas-dev': 'QA, sandbox e ferramentas de importação para administradores',
};

const EmpreendimentosPage: React.FC = () => {
  const { isModuleVisible, isLoading: flagLoading } = useEmpFeatureFlag();
  const { role } = useAuth();
  const { checkModuleAccess, tenantTier } = useTenantTier();
  const location = useLocation();

  if (flagLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isModuleVisible) {
    return <Navigate to="/acesso-negado" replace />;
  }

  const isRoot = location.pathname === '/empreendimentos';

  // ── Route-level tier gating ─────────────────────────────────
  const currentModuleId = location.pathname
    .replace('/empreendimentos/', '')
    .split('/')[0] || 'dashboard';

  const isAccessible = isRoot || checkModuleAccess(currentModuleId);

  if (!isAccessible) {
    return (
      <EmpLayout title="Acesso Restrito">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <ShieldAlert className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Módulo Indisponível</h2>
          <p className="text-muted-foreground max-w-md">
            Este módulo requer um plano superior ao atual (<strong>{tenantTier}</strong>).
            Entre em contato com o administrador para upgrade.
          </p>
        </div>
      </EmpLayout>
    );
  }

  const currentSection = isRoot ? '' : (location.pathname.replace('/empreendimentos/', '').split('/')[0] || '');
  const pageTitle = isRoot ? 'Início' : (sectionLabels[currentSection] || currentSection);
  const pageSubtitle = sectionSubtitles[currentSection];

  return (
    <EmpLayout
      title={pageTitle}
      subtitle={pageSubtitle}
    >
      {isRoot ? <EmpHome /> : <Outlet />}
    </EmpLayout>
  );
};

export default EmpreendimentosPage;
